import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { handleAnchorClick } from "../lib/scrollToAnchor";
import ThemeToggle from "./ThemeToggle";

const links = [
  { label: "Work", href: "#work" },
  { label: "Experience", href: "#experience" },
  { label: "Skills", href: "#skills" },
  { label: "Contact", href: "#contact" },
];

export default function Nav({ theme, onToggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-2xl bg-surface/80 border-b border-line/10 shadow-sm"
          : "bg-black/25 backdrop-blur-md border-b border-white/10"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => handleAnchorClick(e, "#top")}
          data-testid="nav-logo"
          className={`font-display text-xl md:text-2xl tracking-[-0.04em] flex items-center gap-2 transition-colors duration-300 ${
            scrolled ? "text-ink" : "text-white drop-shadow-sm"
          }`}
        >
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
              scrolled ? "bg-ink" : "bg-white"
            }`}
          />
          G-Folio
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => handleAnchorClick(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`font-body text-sm font-medium transition-colors duration-300 ${
                scrolled
                  ? "text-ink-2 hover:text-ink"
                  : "text-white/85 hover:text-white drop-shadow-sm"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} overHero={!scrolled} />
          <a
            href="#contact"
            onClick={(e) => handleAnchorClick(e, "#contact")}
            data-testid="nav-contact-btn"
            className={`hidden md:inline-flex items-center gap-2 px-5 h-9 rounded-full font-body text-sm font-medium transition-all duration-300 active:scale-95 ${
              scrolled
                ? "bg-ink text-surface hover:bg-ink-metal"
                : "bg-white text-black hover:bg-[#e8e8ed] shadow-md"
            }`}
          >
            Get in touch
          </a>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden h-9 w-9 grid place-items-center rounded-full border transition-colors duration-300 ${
              scrolled
                ? "border-line/10 text-ink"
                : "border-white/25 text-white"
            }`}
            aria-label="Toggle menu"
          >
            <span className="block w-4 h-px bg-current relative before:content-[''] before:absolute before:-top-1.5 before:left-0 before:w-4 before:h-px before:bg-current after:content-[''] after:absolute after:top-1.5 after:left-0 after:w-4 after:h-px after:bg-current" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t border-line/5 bg-surface/90 backdrop-blur-2xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => {
                    handleAnchorClick(e, l.href);
                    setOpen(false);
                  }}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-display text-2xl tracking-tight text-ink"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => {
                  handleAnchorClick(e, "#contact");
                  setOpen(false);
                }}
                data-testid="nav-mobile-contact"
                className="mt-2 inline-flex items-center justify-center px-5 h-11 rounded-full bg-ink text-surface font-body text-sm font-medium"
              >
                Get in touch
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
